// modules/freshcards/src/core/overlays/catalog.ts
//
// 2026-09-18 (PROMPT F 20 Phase 4) — Overlay catalog.
//
// Ships the canonical overlays:
//   - default:        current FreshCards UI (no overrides)
//   - freshcards-slate: muted slate palette from app-overlays/freshcards-slate
//   - gallery:        stub (not yet authored)
//   - vibecoder:      stub (not yet authored)
//   - homeschool:     stub (not yet authored)
//
// Stubs have `flavor: 'bundle'` + minimal valid Theme but displayName="Not
// yet authored" so the picker can render them in the catalog with a clear
// state without crashing.
//
// Each catalog entry has a stable id and is registered on first import via
// `registerCatalogOnLoad()`. Idempotent — safe to call multiple times.

import type { AppOverlay } from '../../types/models/AppOverlay';
import type { Theme } from '../../integration/embedding/ThemeAPI';
import { DEFAULT_OVERLAY_ID } from './resolver';
import { getOverlayRegistry } from './registry';

// ---------------------------------------------------------------------------
// Catalog entry type
// ---------------------------------------------------------------------------

export interface CatalogEntry {
  /** Stable id (matches AppOverlay.id). */
  id: string;
  /** Human-readable name shown in picker. */
  displayName: string;
  /** Short description. */
  description: string;
  /** Status — 'ready' for shipped overlays, 'stub' for not-yet-authored. */
  status: 'ready' | 'stub';
  /** Tags for search. */
  tags: string[];
  /** The overlay manifest (valid). */
  overlay: AppOverlay;
  /** Optional thumbnail (data URL or hosted URL). */
  thumbnail?: string;
}

// ---------------------------------------------------------------------------
// Default theme — minimal valid Theme that matches the legacy ThemeAPI shape
// ---------------------------------------------------------------------------

function emptyTheme(accent: string): Theme {
  return {
    palette: {
      accent,
      bg: '#ffffff',
      sub: '#f7f7f7',
      text: '#1a1a1a',
      textMuted: '#666666',
      border: '#e5e5e5',
    },
  } as unknown as Theme;
}

function computeHashStable(s: string): string {
  // djb2 — matches overlayBundle.ts so we don't ship different hashes for the same overlay
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
    h = h | 0;
  }
  const hex = (h >>> 0).toString(16);
  return (hex + hex + hex + hex).slice(0, 32); // 32 chars (padded)
}

function buildOverlay(partial: Omit<AppOverlay, 'bundleHash'>): AppOverlay {
  const hash = computeHashStable(JSON.stringify({ ...partial, bundleHash: '__pending__' }));
  return { ...partial, bundleHash: hash };
}

// ---------------------------------------------------------------------------
// 1. Default overlay (current UI)
// ---------------------------------------------------------------------------

const defaultOverlay = buildOverlay({
  id: DEFAULT_OVERLAY_ID,
  name: 'FreshCards Default',
  description: 'Current FreshCards UI with no overrides. The fallback when no overlay is selected.',
  version: '1.0.0',
  flavor: 'bundle',
  distribution: 'proprietary',
  theme: emptyTheme('#3b82f6'), // blue accent (matches current default)
  author: { id: 'freshcards', displayName: 'FreshCards' },
  license: { spdx: 'proprietary' },
  compatibility: { freshcards: '>=1.0.0' },
  tags: ['default', 'system'],
});

// ---------------------------------------------------------------------------
// 2. freshcards-slate (from app-overlays/freshcards-slate/accent.json)
// ---------------------------------------------------------------------------

const slateOverlay = buildOverlay({
  id: 'overlay.freshcards.slate',
  name: 'FreshCards — Slate',
  description: 'Notion-style muted slate. Calm, neutral, lets content breathe. From app-overlays/freshcards-slate.',
  version: '1.0.0',
  flavor: 'theme',
  distribution: 'off-the-shelf',
  theme: emptyTheme('#475569'), // slate-600 per accent.json
  author: { id: 'freshcards', displayName: 'FreshCards' },
  license: { spdx: 'MIT' },
  compatibility: { freshcards: '>=1.0.0' },
  tags: ['slate', 'notion', 'minimal', 'neutral'],
});

// ---------------------------------------------------------------------------
// 2b-2c. F 109 — Clay + Paper & Ink, promoted from the F 108
// skin-candidates page. Same AppOverlay shape as slate; no new types.
// Warm Slate remains the default (DEFAULT_OVERLAY_ID untouched).
// ---------------------------------------------------------------------------

const clayOverlay = buildOverlay({
  id: 'overlay.freshcards.clay',
  name: 'FreshCards — Clay',
  description: 'Terracotta-forward surfaces, muted olive accent. Earthy and sun-dried. From app-overlays/freshcards-clay.',
  version: '1.0.0',
  flavor: 'theme',
  distribution: 'off-the-shelf',
  theme: emptyTheme('#6E7A54'), // clay accent.primary per accent.json
  author: { id: 'freshcards', displayName: 'FreshCards' },
  license: { spdx: 'MIT' },
  compatibility: { freshcards: '>=1.9.0' },
  tags: ['clay', 'terracotta', 'earthy', 'warm'],
});

const paperInkOverlay = buildOverlay({
  id: 'overlay.freshcards.paper-ink',
  name: 'FreshCards — Paper & Ink',
  description: 'Warm white, near-black text, one bold warm-red accent. Highest contrast. From app-overlays/freshcards-paper-ink.',
  version: '1.0.0',
  flavor: 'theme',
  distribution: 'off-the-shelf',
  theme: emptyTheme('#B4392E'), // paper-ink accent.primary per accent.json
  author: { id: 'freshcards', displayName: 'FreshCards' },
  license: { spdx: 'MIT' },
  compatibility: { freshcards: '>=1.9.0' },
  tags: ['paper', 'ink', 'high-contrast', 'editorial'],
});

// ---------------------------------------------------------------------------
// 3-5. Stubs (not yet authored — display only, cannot be applied without
// real overlay data)
// ---------------------------------------------------------------------------

function stub(id: string, name: string, description: string, tags: string[]): AppOverlay {
  return buildOverlay({
    id,
    name,
    description,
    version: '0.0.0',
    flavor: 'bundle',
    distribution: 'generic',
    theme: emptyTheme('#888888'),
    author: { id: 'system', displayName: 'System' },
    license: { spdx: 'proprietary', notes: 'Not yet authored — stub entry for catalog UI.' },
    compatibility: { freshcards: '>=0.0.0' },
    tags: [...tags, 'stub'],
  });
}

const galleryStub = stub(
  'overlay.gallery.stub',
  'Gallery — Not yet authored',
  'Stub. Real Gallery overlay pending operator authoring.',
  ['gallery', 'consumer'],
);

const vibecoderStub = stub(
  'overlay.vibecoder.stub',
  'VibeCoder — Not yet authored',
  'Stub. Real VibeCoder overlay pending operator authoring.',
  ['vibecoder', 'consumer'],
);

const homeschoolStub = stub(
  'overlay.homeschool.stub',
  'Homeschool — Not yet authored',
  'Stub. Real Homeschool overlay pending operator authoring.',
  ['homeschool', 'consumer'],
);

// ---------------------------------------------------------------------------
// Catalog (full list)
// ---------------------------------------------------------------------------

export const CATALOG: CatalogEntry[] = [
  {
    id: defaultOverlay.id,
    displayName: 'FreshCards Default',
    description: defaultOverlay.description ?? '',
    status: 'ready',
    tags: ['default', 'system'],
    overlay: defaultOverlay,
  },
  {
    id: slateOverlay.id,
    displayName: 'FreshCards — Slate',
    description: slateOverlay.description ?? '',
    status: 'ready',
    tags: ['slate', 'notion', 'minimal', 'neutral'],
    overlay: slateOverlay,
  },
  {
    id: clayOverlay.id,
    displayName: 'Clay',
    description: clayOverlay.description ?? '',
    status: 'ready',
    tags: ['clay', 'terracotta', 'earthy', 'warm'],
    overlay: clayOverlay,
  },
  {
    id: paperInkOverlay.id,
    displayName: 'Paper & Ink',
    description: paperInkOverlay.description ?? '',
    status: 'ready',
    tags: ['paper', 'ink', 'high-contrast', 'editorial'],
    overlay: paperInkOverlay,
  },
  {
    id: galleryStub.id,
    displayName: 'Gallery',
    description: 'Consumer overlay for fv-module-gallery. Stub — not yet authored.',
    status: 'stub',
    tags: ['gallery', 'consumer', 'stub'],
    overlay: galleryStub,
  },
  {
    id: vibecoderStub.id,
    displayName: 'VibeCoder',
    description: 'Consumer overlay for vibecoder-standalone. Stub — not yet authored.',
    status: 'stub',
    tags: ['vibecoder', 'consumer', 'stub'],
    overlay: vibecoderStub,
  },
  {
    id: homeschoolStub.id,
    displayName: 'Homeschool',
    description: 'Consumer overlay for homeschool. Stub — not yet authored.',
    status: 'stub',
    tags: ['homeschool', 'consumer', 'stub'],
    overlay: homeschoolStub,
  },
];

/**
 * Register all catalog entries with the OverlayRegistry. Idempotent — safe
 * to call multiple times; the registry rejects duplicates.
 */
export function registerCatalogOnLoad(): void {
  const reg = getOverlayRegistry();
  // Always set default first (so resolve() falls back to it before any user
  // has applied anything).
  reg.setDefault(defaultOverlay);
  for (const entry of CATALOG) {
    if (!reg.has(entry.overlay.id)) {
      reg.register(entry.overlay);
    }
  }
}

/** Lookup helper for the UI layer. */
export function findCatalogEntry(id: string): CatalogEntry | null {
  return CATALOG.find((e) => e.id === id) ?? null;
}

/** Filter helper for the picker. */
export function filterCatalog(opts: { status?: 'ready' | 'stub'; tag?: string }): CatalogEntry[] {
  return CATALOG.filter((e) => {
    if (opts.status && e.status !== opts.status) return false;
    if (opts.tag && !e.tags.includes(opts.tag)) return false;
    return true;
  });
}
